import { createElement } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { t } from 'i18next';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { log } from '@/logger/logger';
import { useGlobalStore } from '@/state/global.state';
import { WarningCircleIcon } from '@/UI/Icons';
import { I18N_KEYS } from '@/language/keys';
import type { ModelDefinition } from '@/types/api/modelDefinition';

const logger = log.getLogger('SettingsStore');

type LoadingState = 'initial' | 'loading' | 'loaded' | 'error';

interface SettingsState {
  workspaceModels: ModelDefinition[];
  workspaceModelsLoadingState: LoadingState;
  updatingModelIds: Set<string>;
  loadWorkspaceModels: (workspaceId: string, origins?: string[], signal?: AbortSignal) => Promise<void>;
  updateModelAllowedStatus: (workspaceId: string, modelId: string, allowed: boolean) => Promise<void>;
  resetSettingsState: () => void;
}

const createInitialState = () => ({
  workspaceModels: [] as ModelDefinition[],
  workspaceModelsLoadingState: 'initial' as LoadingState,
  updatingModelIds: new Set<string>(),
});

export const useSettingsStore = create<SettingsState>()(
  devtools(
    (set, get) => ({
      ...createInitialState(),
      loadWorkspaceModels: async (workspaceId: string, origins?: string[], signal?: AbortSignal) => {
        if (get().workspaceModelsLoadingState === 'loaded') {
          return;
        }
        set({ workspaceModelsLoadingState: 'loading' });
        try {
          const params = origins?.length ? new URLSearchParams(origins.map((o) => ['origin', o])) : undefined;
          const response = await safeAxios.get<{ models: ModelDefinition[] }>(
            API_ROUTES.workspaces.models({ workspaceId }),
            {
              params,
              signal,
            },
          );
          const models = response.data.models.map((m) => ({
            ...m,
            createdAt: m.createdAt ?? m.requestedAt,
          }));
          set({ workspaceModels: models, workspaceModelsLoadingState: 'loaded' });
        } catch (error) {
          // Don't update state if request was cancelled
          if (signal?.aborted) {
            logger.info('Workspace models request was cancelled');
            return;
          }
          logger.error('Error loading workspace models', error);
          set({ workspaceModelsLoadingState: 'error' });
        }
      },
      updateModelAllowedStatus: async (workspaceId: string, modelId: string, isAllowed: boolean) => {
        // Optimistic update: Update UI immediately
        set((state) => ({
          workspaceModels: state.workspaceModels.map((m) => (m.modelId === modelId ? { ...m, isAllowed } : m)),
          updatingModelIds: new Set(state.updatingModelIds).add(modelId),
        }));

        try {
          await safeAxios.put(API_ROUTES.workspaces.models({ workspaceId }), {
            models: [
              {
                modelId,
                isAllowed,
              },
            ],
          });
          set((state) => {
            const newUpdatingIds = new Set(state.updatingModelIds);
            newUpdatingIds.delete(modelId);
            return { updatingModelIds: newUpdatingIds };
          });
        } catch (error) {
          logger.error('Error updating model allowed status', error);
          // Revert optimistic update and show error snackbar
          set((state) => {
            const newUpdatingIds = new Set(state.updatingModelIds);
            newUpdatingIds.delete(modelId);

            return {
              workspaceModels: state.workspaceModels.map((m) =>
                m.modelId === modelId ? { ...m, isAllowed: !isAllowed } : m,
              ),
              updatingModelIds: newUpdatingIds,
            };
          });

          useGlobalStore.getState().updateSnackbarData({
            text: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.ERROR_UPDATING_APPROVED),
            isOpen: true,
            severity: 'error',
            icon: createElement(WarningCircleIcon, { width: 20, height: 20 }),
          });
        }
      },
      resetSettingsState: () => set(createInitialState()),
    }),
    {
      name: 'Settings Store',
      enabled: import.meta.env.DEV,
    },
  ),
);
